import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { reconcileGrid, dropOrphanedScreens } from '../devices/device-grid';
import { can } from '../core/access';
import { lastSeenIndicatorHtml } from '../core/format';
import { deviceSubtitlesHtml, syncDeviceSubtitlesIn, deviceNameHtml } from '../devices/device-card';
import { showInfoModal, showDeviceCommentModal, showRenameDeviceModal } from '../devices/device-modals';
import { showClickerRestartModal } from '../clicker/clicker-restart-modal';
import { openClickerLogWindow } from '../clicker/clicker-info-modal';
import { openClickerOperationsWindow } from '../clicker/clicker-operations-modal';
import { openClickerHistoryWindow } from '../clicker/clicker-history-modal';
import { canStopClicker, confirmClickerStop, stopBlockedReason } from '../clicker/clicker-shared';
import { closeShiftBtnHtml, syncCloseShiftBtn, wireCloseShiftBtn } from '../clicker/close-shift-shared';
import { connectIconSvg, disconnectIconSvg, clickerStartSvg, clickerStopSvg, clickerSettingsSvg, clickerLogSvg, clickerRegistrySvg, clickerHistorySvg, phoneGlyphSvg, infoSvg, commentSvg, adbSvg, } from '../stream/stream-controls-shared';
const GEELARK_POLL_MS = 20000; // server registry refreshes every 60s; poll a bit under
// Keep a connected screen across brief absences of its phone from a single sync pass.
const GEELARK_STREAM_GRACE_MS = 90000;
let geelarkPoll = null;
const geelarkConnected = new Map();
// The GeeLark remote-access page renders at a fixed size and ignores a bigger iframe
// viewport (it centres itself and pads the rest with white), so the frame is laid out at
// exactly that size and scaled to fill the card's screen area instead. Keep in step with
// .glk-frame in style.css.
const GLK_FRAME_W = 395;
const GLK_FRAME_H = 690;
/** Scale the fixed-size embed to fill `wrap` (contain fit, so nothing is cropped). */
function applyGlkZoom(wrap) {
    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    if (!w || !h)
        return;
    wrap.style.setProperty('--glk-zoom', String(Math.min(w / GLK_FRAME_W, h / GLK_FRAME_H)));
}
// Re-fit on any change of the screen area — the card-size / cards-per-row sliders and
// window resizes all land here, so the zoom never goes stale.
const glkZoomObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver((entries) => { for (const e of entries)
        applyGlkZoom(e.target); })
    : null;
const warningSvg = '<svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>';
const spinnerSvg = '<svg class="duo-spin" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M21 12a9 9 0 1 0-9 9"/></svg>';
/** Show a message over the screen area (used when a connect attempt fails). */
function setGeelarkStatus(card, text) {
    const wrap = card.querySelector('.glk-screen-wrap');
    if (wrap)
        wrap.innerHTML = `<div class="device-player-placeholder"><span>${warningSvg}</span><strong>${esc(text)}</strong></div>`;
}
/** Connect: fetch the phone's GeeLark remote-access URL and embed it in the card iframe.
 *  Lazy — only fired by the Connect button, so no share is created for an unwatched phone. */
async function connectGeelark(card, d, farmId) {
    if (geelarkConnected.has(d.udid))
        return;
    geelarkConnected.set(d.udid, { url: '', lastSeen: Date.now() });
    renderGeelarkScreen(card, d); // "Connecting…" + Disconnect button
    try {
        const r = await fetch(`/api/farms/${farmId}/geelark-devices/${encodeURIComponent(d.udid)}/share`, { cache: 'no-store' });
        if (!r.ok) {
            const data = await r.json().catch(() => ({}));
            geelarkConnected.delete(d.udid);
            renderGeelarkScreen(card, d);
            setGeelarkStatus(card, data.error || t('farmdev.screen_unavailable', { status: r.status }));
            return;
        }
        const { url } = await r.json();
        const c = geelarkConnected.get(d.udid);
        if (!c)
            return; // disconnected while awaiting the link
        c.url = url;
        renderGeelarkScreen(card, d); // paint the iframe
    }
    catch {
        geelarkConnected.delete(d.udid);
        renderGeelarkScreen(card, d);
        setGeelarkStatus(card, t('farmdev.network_error'));
    }
}
/** Disconnect: drop the embedded screen (the iframe is torn out on the next render). */
function disconnectGeelark(udid) {
    geelarkConnected.delete(udid);
}
/** Paint the screen area + connect button for the card's current connection state. */
function renderGeelarkScreen(card, d) {
    const wrap = card.querySelector('.glk-screen-wrap');
    const btn = card.querySelector('.glk-connect');
    if (!wrap || !btn)
        return;
    const c = geelarkConnected.get(d.udid);
    if (c) {
        if (c.url) {
            wrap.innerHTML = `<iframe class="duo-screen-frame glk-frame" src="${esc(c.url)}" title="${esc(d.name)}"
                allow="clipboard-read; clipboard-write"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"></iframe>`;
            applyGlkZoom(wrap);
            glkZoomObserver?.observe(wrap);
        }
        else {
            glkZoomObserver?.unobserve(wrap);
            wrap.innerHTML = `<div class="device-player-placeholder"><span>${spinnerSvg}</span><strong>${t('farmdev.connecting')}</strong></div>`;
        }
        btn.innerHTML = disconnectIconSvg;
        btn.title = t('farmdev.disconnect');
        btn.setAttribute('aria-label', btn.title);
    }
    else {
        wrap.innerHTML = `<div class="device-player-placeholder"><span>${phoneGlyphSvg}</span><strong>${t('farmdev.not_connected')}</strong></div>`;
        btn.innerHTML = connectIconSvg;
        btn.title = t('farmdev.connect');
        btn.setAttribute('aria-label', btn.title);
    }
}
/** Reflect the clicker Start/Stop button + name subtitle for the current run-state. The
 *  Start button is disabled until ADB is up (a non-empty adb endpoint) — the clicker runs
 *  over adb, so starting it without ADB would just fail. */
function renderGeelarkClicker(card, d) {
    const btn = card.querySelector('.glk-clicker');
    if (btn) {
        const running = !!d.clickerRunning;
        btn.innerHTML = running ? clickerStopSvg : clickerStartSvg;
        btn.classList.toggle('is-running', running);
        // Gate Start on the adb endpoint; a running clicker keeps its Stop button live.
        btn.disabled = !running && !d.adb;
        // Somebody else's run: a plain operator cannot stop it (the server refuses it too).
        const blocked = running && !canStopClicker(d.clickerStartedBy);
        if (blocked)
            btn.disabled = true;
        btn.title = blocked
            ? stopBlockedReason(d.clickerStartedBy)
            : btn.disabled ? t('farmdev.enable_adb_to_start') : (running ? t('farmdev.stop_clicker') : t('farmdev.start_clicker'));
        btn.setAttribute('aria-label', btn.title);
    }
    syncCloseShiftBtn(card, 'glk-clicker-shift', !!d.clickerRunning);
    const wrap = card.querySelector('.device-name-wrap');
    if (wrap) {
        syncDeviceSubtitlesIn(wrap, d);
    }
}
/** Reflect the ADB toggle button: green when ADB is enabled, red when disabled. */
function renderGeelarkAdb(card, d) {
    const btn = card.querySelector('.glk-adb');
    if (!btn)
        return;
    const on = !!d.adbEnabled;
    btn.classList.toggle('adb-on', on);
    btn.classList.toggle('adb-off', !on);
    btn.title = on ? t('farmdev.adb_on_hint') : t('farmdev.adb_off_hint');
    btn.setAttribute('aria-label', btn.title);
}
function makeGeelarkDeviceCard(d, farmId, farmLabel) {
    const card = document.createElement('div');
    // --duo carries the shared cloud-card behaviour (and is the grid's reconcile selector);
    // --geelark only narrows the card so the embedded GeeLark page letterboxes less.
    card.className = 'device-card device-card--duo device-card--geelark online';
    card.dataset['udid'] = d.udid;
    const logBtn = can('device.clickerLog')
        ? `<button type="button" class="stream-control-btn glk-clicker-logs" title="${t('farmdev.clicker_logs')}" aria-label="${t('farmdev.clicker_logs')}">${clickerLogSvg}</button>`
        : '';
    const historyBtn = can('device.clickerHistory')
        ? `<button type="button" class="stream-control-btn glk-clicker-history" title="${t('farmdev.clicker_history')}" aria-label="${t('farmdev.clicker_history')}">${clickerHistorySvg}</button>`
        : '';
    const adbBtn = can('device.cloudAdb')
        ? `<button type="button" class="stream-control-btn glk-adb" title="ADB" aria-label="ADB">${adbSvg}</button>`
        : '';
    card.innerHTML = `
        <div class="device-card__header">
            <span class="status-dot status-dot--green"></span>
            <span class="device-name-wrap">
                ${deviceNameHtml(d.name, d.udid, farmLabel)}
                ${deviceSubtitlesHtml(d)}
            </span>
            ${lastSeenIndicatorHtml(d.lastSeen, 'glk-updated')}
            <button class="stream-btn glk-connect" title="${t('farmdev.connect')}" aria-label="${t('farmdev.connect')}">${connectIconSvg}</button>
        </div>
        <div class="device-card__player">
            <div class="device-stream-stage">
                <div class="device-stream-canvas-wrap glk-screen-wrap"></div>
                <div class="device-stream-controls" aria-label="${t('farmdev.device_controls')}">
                    <button type="button" class="stream-control-btn glk-info-btn" title="${t('farmdev.device_info')}" aria-label="${t('farmdev.device_info')}">${infoSvg}</button>
                    <button type="button" class="stream-control-btn glk-comment-btn" title="${t('farmdev.comment')}" aria-label="${t('farmdev.comment')}">${commentSvg}</button>
                    <span class="stream-controls__divider"></span>
                    <button type="button" class="stream-control-btn glk-clicker duo-clicker" title="${t('farmdev.start_clicker')}" aria-label="${t('farmdev.start_clicker')}">${clickerStartSvg}</button>
                    <button type="button" class="stream-control-btn glk-clicker-settings" title="${t('farmdev.reconfigure_clicker')}" aria-label="${t('farmdev.reconfigure_clicker')}">${clickerSettingsSvg}</button>
                    ${logBtn}
                    <button type="button" class="stream-control-btn glk-clicker-ops" title="${t('farmdev.operations_registry')}" aria-label="${t('farmdev.operations_registry')}">${clickerRegistrySvg}</button>
                    ${historyBtn}
                    ${closeShiftBtnHtml('glk-clicker-shift')}
                    ${adbBtn ? '<span class="stream-controls__divider"></span>' + adbBtn : ''}
                </div>
            </div>
        </div>
    `;
    renderGeelarkScreen(card, d);
    renderGeelarkClicker(card, d);
    renderGeelarkAdb(card, d);
    wireCloseShiftBtn(card, 'glk-clicker-shift', farmId, d.udid);
    card.querySelector('.glk-info-btn')?.addEventListener('click', () => {
        const rows = [
            { label: t('farmdev.device_id'), value: d.udid },
            { label: t('common.status'), value: t('common.online') },
            { label: 'ADB', value: d.adbEnabled ? t('common.enabled') : t('common.disabled') },
            { label: t('farmdev.adb_endpoint'), value: d.adb || null },
            { label: t('farmdev.clicker'), value: d.clickerRunning ? t('farmdev.running') : t('farmdev.stopped') },
            { label: t('farmdev.app'), value: d.clickerRunning ? (d.clickerApp ?? null) : null },
            { label: t('farmdev.task'), value: d.clickerRunning ? (d.clickerTask ?? null) : null },
        ]
            .filter((r) => !!r.value);
        const extra = can('device.rename')
            ? { label: t('common.rename'), onClick: () => showRenameDeviceModal(d.udid, d.name) }
            : undefined;
        showInfoModal(d.name, rows, extra);
    });
    card.querySelector('.glk-comment-btn')?.addEventListener('click', () => {
        showDeviceCommentModal(d.udid, d.name, d.comment || '');
    });
    card.querySelector('.glk-connect')?.addEventListener('click', () => {
        if (geelarkConnected.has(d.udid)) {
            disconnectGeelark(d.udid);
            renderGeelarkScreen(card, d);
        }
        else
            void connectGeelark(card, d, farmId);
    });
    // ADB enable/disable. Enabling ADB makes the clicker usable; the next sync re-resolves
    // the adb endpoint and repaints. Optimistic flip; the 20s poll reconciles.
    card.querySelector('.glk-adb')?.addEventListener('click', async () => {
        const btn = card.querySelector('.glk-adb');
        if (!btn)
            return;
        const enable = !d.adbEnabled;
        if (!confirm(enable ? t('farmdev.confirm_adb_enable', { name: d.name }) : t('farmdev.confirm_adb_disable', { name: d.name })))
            return;
        btn.disabled = true;
        try {
            const res = await fetch(`/api/farms/${farmId}/geelark-devices/${encodeURIComponent(d.udid)}/adb/${enable ? 'on' : 'off'}`, { method: 'POST' });
            if (res.ok) {
                d.adbEnabled = enable;
                renderGeelarkAdb(card, d);
                // Re-query status now (the server re-synced) so the endpoint + clicker gate
                // update immediately instead of on the next 20s poll.
                void injectGeelarkDevices(farmId);
            }
            else {
                const data = await res.json().catch(() => ({}));
                alert(data.error || t('farmdev.adb_toggle_failed'));
            }
        }
        catch { /* network error — leave state unchanged */ }
        finally {
            btn.disabled = false;
        }
    });
    card.querySelector('.glk-clicker')?.addEventListener('click', async () => {
        const btn = card.querySelector('.glk-clicker');
        if (!btn)
            return;
        const action = d.clickerRunning ? 'stop' : 'start';
        if (action === 'stop' && !canStopClicker(d.clickerStartedBy))
            return;
        if (action === 'stop' && !confirmClickerStop(d.name))
            return;
        btn.disabled = true;
        try {
            const res = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(d.udid)}/${action}`, { method: 'POST' });
            if (res.ok) {
                d.clickerRunning = action === 'start';
                renderGeelarkClicker(card, d);
            }
            else {
                const data = await res.json().catch(() => ({}));
                alert(data.error || t('farmdev.clicker_action_failed'));
            }
        }
        catch { /* network error — leave state unchanged */ }
        finally {
            btn.disabled = false;
        }
    });
    card.querySelector('.glk-clicker-settings')?.addEventListener('click', () => {
        void showClickerRestartModal(farmId, d.udid, d.name);
    });
    card.querySelector('.glk-clicker-logs')?.addEventListener('click', () => openClickerLogWindow(d.udid));
    card.querySelector('.glk-clicker-ops')?.addEventListener('click', () => openClickerOperationsWindow(farmId, d.udid, d.name));
    card.querySelector('.glk-clicker-history')?.addEventListener('click', () => openClickerHistoryWindow(farmId, d.udid, d.name));
    return card;
}
/**
 * Fetch a geelark farm's synced phones. Split out of {@link injectGeelarkDevices} so the
 * My Devices board reuses the same (already user-scoped) endpoint for its cloud sections.
 */
export async function fetchGeelarkDevices(farmId, refresh = false) {
    try {
        const r = await fetch(`/api/farms/${farmId}/geelark-devices${refresh ? '?refresh=1' : ''}`);
        if (!r.ok)
            return { devices: [], failed: true };
        const body = await r.json();
        return { devices: body.devices ?? [], failed: false };
    }
    catch {
        return { devices: [], failed: true };
    }
}
/**
 * Render `data` onto `grid` as this farm's GeeLark cards. Pure render — no fetch — so the
 * per-farm grid and the My Devices board drive the identical reconciliation. Unlike duo,
 * GeeLark grids keep the provider's own order and are not drag-reorderable.
 */
export function injectGeelarkDevicesInto(grid, farmId, data) {
    // GeeLark grids keep the provider's own order and are not drag-reorderable.
    reconcileGrid(grid, data.map((d) => ({
        udid: d.udid,
        preserve: geelarkConnected.has(d.udid), // live iframe — never rebuild it
        make: () => makeGeelarkDeviceCard(d, farmId),
    })), (udid) => geelarkConnected.has(udid));
    dropOrphanedScreens(geelarkConnected.keys(), disconnectGeelark);
}
/** Fetch a geelark farm's synced (powered-on) phones and render them as the device grid.
 *  `opts.refresh` forces a fresh server-side sync (the manual Refresh button). */
export async function injectGeelarkDevices(farmId, opts) {
    const { devices: data, failed } = await fetchGeelarkDevices(farmId, opts?.refresh === true);
    const grid = document.getElementById('device-grid');
    if (!grid || !document.body.contains(grid))
        return;
    // Age out connected screens whose phone left the active set past the grace period.
    const liveUdids = new Set(data.map((d) => d.udid));
    if (!failed) {
        const now = Date.now();
        for (const [udid, c] of geelarkConnected) {
            if (liveUdids.has(udid))
                c.lastSeen = now;
            else if (now - c.lastSeen > GEELARK_STREAM_GRACE_MS)
                disconnectGeelark(udid);
        }
    }
    injectGeelarkDevicesInto(grid, farmId, data);
    if (!grid.querySelector('.device-card--geelark')) {
        const empty = document.createElement('p');
        empty.className = 'empty';
        empty.textContent = failed
            ? t('farmdev.geelark_unreachable')
            : t('farmdev.geelark_no_devices');
        grid.appendChild(empty);
    }
}
/** Stop the active geelark device-grid poll. `keepFarmName` leaves it running when it
 *  matches the farm currently polled (a re-render of the SAME farm keeps the loop). */
export function stopGeelarkDevicePolling(keepFarmName) {
    if (!geelarkPoll)
        return;
    if (keepFarmName !== undefined && geelarkPoll.farmName === keepFarmName)
        return;
    window.clearInterval(geelarkPoll.timer);
    geelarkPoll = null;
    // Only drop screens whose card actually left the page — the My Devices board can hold
    // cards of this same farm, and leaving the farm grid must not reset those.
    dropOrphanedScreens(geelarkConnected.keys(), disconnectGeelark);
}
/** Poll {@link injectGeelarkDevices} while this geelark farm's grid stays mounted. */
export function startGeelarkDevicePolling(farmId, farmName) {
    const grid = document.getElementById('device-grid');
    if (!grid)
        return;
    if (geelarkPoll && geelarkPoll.farmName !== farmName)
        stopGeelarkDevicePolling();
    else if (geelarkPoll)
        window.clearInterval(geelarkPoll.timer);
    const timer = window.setInterval(() => {
        const g = document.getElementById('device-grid');
        if (!g || !document.body.contains(g)) {
            stopGeelarkDevicePolling();
            return;
        }
        void injectGeelarkDevices(farmId);
    }, GEELARK_POLL_MS);
    geelarkPoll = { farmName, timer };
    void injectGeelarkDevices(farmId);
}
/** Build one geelark card for a grid that mixes farms (the My Devices board), with the
 *  owning farm shown in front of the device name. Same builder as the farm grid uses. */
export function makeGeelarkCardForBoard(d, farmId, farmLabel) {
    return makeGeelarkDeviceCard(d, farmId, farmLabel);
}
/** Whether this phone currently holds a live embedded screen — the board needs it to
 *  know which cards it must not rebuild. */
export function isGeelarkConnected(udid) {
    return geelarkConnected.has(udid);
}
